/* eslint max-lines: 0 */
import moment from 'moment';
import qs from 'qs';
import { StatusCodes } from 'http-status-codes';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { translate } from 'services/i18nService';
import MessagingService from 'services/MessagingService/MessagingService';
import {
  GET_GALLERIES,
  GET_GALLERIES_ERROR,
  GET_GALLERIES_RESPONSE,
  GET_GALLERIES_RESPONSE_STATE,
  GET_FEATURED_CAM_STATS,
  GET_PEEK_STREAMING_INFO,
  GET_PEEK_STREAMING_INFO_RESPONSE,
  GET_PEEK_STREAMING_INFO_ERROR,
  GET_PHOTOS,
  GET_PHOTOS_RESPONSE,
  GET_PHOTOS_RESPONSE_STATE,
  GET_REPORT_USER_INFO,
  GET_REPORT_USER_INFO_RESPONSE,
  GET_REPORT_USER_INFO_RESPONSE_STATUS,
  GET_REPORT_USER_RESPONSE_STATUS,
  GET_SEX_QUESTIONNAIRE,
  GET_SEX_QUESTIONNAIRE_RESPONSE,
  GET_SILENCE_USER_RESPONSE_STATUS,
  GET_SILENCE_LIST_RESPONSE,
  GET_SILENCE_LIST_ERROR,
  GET_SILENCE_LIST,
  GET_STREAMING_INFO,
  GET_STREAMING_INFO_RESPONSE,
  GET_STREAMING_INFO_ERROR,
  GET_SIMILAR_CAMS,
  GET_USER_FRIENDS,
  GET_USER_FRIENDS_RESPONSE,
  PERFORMER_HAS_BEEN_FEATURED,
  POST_FEEDBACK_V2_PROFILE_RESPONSE_STATUS,
  REPORT_USER,
  ROOM_HAS_BEEN_CLOSED,
  SEND_USER_FEEDBACK_MODAL,
  SILENCE_USER,
  SILENCE_OR_BAN_USER_AS_MODERATOR,
  WISHLIST_INFO_RESPONSE,
  WISHLIST_INFO,
} from 'services/MessagingService/EventNames';
import ERRORS from 'services/MessagingService/Errors';
import NotificationService from 'services/NotificationService';
import FirebaseStore from 'stores/FirebaseStore';
import UserStore from 'stores/UserStore';
import { DIRECTORY_URL } from 'pages/Directory/Api';
import APIStates from 'enum/APIStates';
import { REQUEST_STATUS } from 'enum/helpers';
import {
  ENUM_FAVOURITE_STATUS,
  ENUM_FRIEND_STATUS, HEIGHT_UNIT,
} from 'enum/profile';
import parseCatch from 'utils/parseCatch';
import {
  generateQueryFromObject,
  isNotEmpty,
} from 'utils/helpers';
import { STREAM_PROVIDERS } from 'utils/consts';


const WEBCHAT_HOST = ConfigurationService.get(ConfigurationKeys.WEB_CHAT_HOST);
const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);
const SIMILAR_CAMS_COUNT = 16;

const GET_PERFORMER_DETAILED_INFO_URL = `${BASE_URL}/rest/v1.0/profile/{username}/info`;
const GET_PERFORMER_GIFTS_URL = `${BASE_URL}/rest/v1.0/profile/{username}/gifts`;
const PERFORMER_FANCLUB_URL = `${BASE_URL}/rest/v1.0/profile/{username}/fanclub`;
const FRIENDS_STATUS_URL = `${BASE_URL}/rest/v1.0/friends/{username}/{performerName}`;
const FAVOURITES_BASE = `${BASE_URL}/rest/v1.0/favorites/{username}/{performerName}`;
const FAVOURITES_COUNT = `${BASE_URL}/rest/v1.0/favorites/{performerName}/count`;
const PERFORMER_GALLERIES_URL_NEW = `${BASE_URL}/rest/v2.0/users/{username}/galleries`;
const UPCOMING_SHOWS_URL = `${BASE_URL}/rest/v1.0/users/{username}/scheduled-shows`;
const STREAMING_INFO_URL = `${BASE_URL}/rest/v1.0/profile/{username}/streamInfo`;
const LOAD_CONVERSATIONS_URL = `${BASE_URL}/loadConversations`;
const SILENCE_USER_URL = `${BASE_URL}/rest/v1.0/profile/{username}/silence/{silenceTarget}`;
const GET_SILENCE_LIST_URL = `${BASE_URL}/rest/v1.0/profile/{username}/silence`;
const WEBCHAT_SEND_NOTIFICATION = `${WEBCHAT_HOST}sendNotification`;
const REPORT_USER_URL = `${BASE_URL}/rest/v1.0/abuse-reports`;
const FILE_UPLOAD_URL = `${BASE_URL}/testCommunicatorFileUpload`;
const WISHLIST_URL = `${BASE_URL}/rest/v1.0/wishList/{username}`;
const IMAGES_URL_NEW = `${BASE_URL}/rest/v2.0/users/{username}/galleries/{galleryId}/images?{status}`;
const FRIENDLIST_URL = `${BASE_URL}/rest/v1.0/friends/{username}`;
const SEX_QUESTIONNAIRES_URL = `${BASE_URL}/rest/v1.0/sex-questionnaires/{username}`;
const PROFILE_V2_FEEDBACK_URL = `${BASE_URL}/rest/v1.0/users/{username}/profile-feedback`;
const BROADCAST_FEATURED = `${BASE_URL}/rest/v1.0/featured-cams`;

class ProfileService {
  constructor(messagingService) {
    this.messagingService = messagingService();
    // [TODO] Initialization should be done at a top level to Solve all dependencies
    this.messagingService.on(GET_GALLERIES, this.getGalleries);
    this.messagingService.on(GET_STREAMING_INFO, this.getStreamingInfo);
    this.messagingService.on(GET_SILENCE_LIST, this.getSilenceList);
    this.messagingService.on(SILENCE_USER, this.silenceUser);
    this.messagingService.on(SILENCE_OR_BAN_USER_AS_MODERATOR, this.silenceOrBanUserAsRoomModerator);
    this.messagingService.on(REPORT_USER, this.reportUser);
    this.messagingService.on(WISHLIST_INFO, this.getWishlist);
    this.messagingService.on(GET_PHOTOS, this.getPhotos);
    this.messagingService.on(GET_REPORT_USER_INFO, this.getReportUserInfo);
    this.messagingService.on(GET_USER_FRIENDS, this.getUsersFriends);
    this.messagingService.on(GET_SEX_QUESTIONNAIRE, this.getSexQuestionnaire);
    this.messagingService.on(PERFORMER_HAS_BEEN_FEATURED, this._featureBroadcasterAction);
    this.messagingService.on(ROOM_HAS_BEEN_CLOSED, this._closeRoomAction);
    this.messagingService.on(GET_PEEK_STREAMING_INFO, this.getPeekStreamingInfo);
  }

  destructor() {
    this.messagingService.off(GET_GALLERIES, this.getGalleries);
    this.messagingService.off(GET_STREAMING_INFO, this.getStreamingInfo);
    this.messagingService.off(GET_SILENCE_LIST, this.getSilenceList);
    this.messagingService.off(SILENCE_USER, this.silenceUser);
    this.messagingService.off(SILENCE_OR_BAN_USER_AS_MODERATOR, this.silenceOrBanUserAsRoomModerator);
    this.messagingService.off(REPORT_USER, this.reportUser);
    this.messagingService.off(WISHLIST_INFO, this.getWishlist);
    this.messagingService.off(GET_PHOTOS, this.getPhotos);
    this.messagingService.off(GET_REPORT_USER_INFO, this.getReportUserInfo);
    this.messagingService.off(GET_USER_FRIENDS, this.getUsersFriends);
    this.messagingService.off(GET_SEX_QUESTIONNAIRE, this.getSexQuestionnaire);
    this.messagingService.off(PERFORMER_HAS_BEEN_FEATURED, this._featureBroadcasterAction);
    this.messagingService.off(ROOM_HAS_BEEN_CLOSED, this._closeRoomAction);
    this.messagingService.off(GET_PEEK_STREAMING_INFO, this.getPeekStreamingInfo);
  }

  getProfileInfo = (performerName = '') => {
    const callUrl = ApiService.substitute(GET_PERFORMER_DETAILED_INFO_URL, {
      username: performerName.toLowerCase(),
    });

    return ApiService.get(encodeURI(callUrl)).then((res) => {
      if (res.data) {
        if (res.data.city && res.data.country) {
          res.data.location = `${res.data.city}, ${res.data.country}`;
        } else if (res.data.city) {
          res.data.location = res.data.city;
        } else if (res.data.country) {
          res.data.location = res.data.country;
        } else {
          res.data.location = '';
        }
        if (res.data.height) {
          if (res.data.heightUnit === HEIGHT_UNIT.METRIC) {
            res.data.displayHeight = `${res.data.height} cm`;
          }
          if (res.data.heightUnit === HEIGHT_UNIT.IMPERIAL) {
            res.data.displayHeight = res.data.height.replace(',', '\'').concat('"');
          }
        }
        if (res.data.bodyDecorations && res.data.bodyDecorations.length) {
          res.data.bodyDecorations = res.data.bodyDecorations.filter((bd) => bd !== 'other' && bd !== 'bodyPiercings');
        }

        const propertyToIgnore = ['countryId', 'mainLanguage', 'heightUnit'];
        const allPerformerCompletedData =
          res.data &&
          Object.keys(res.data).filter((key) => {
            if (key === 'wishList') {
              return Object.values(res.data[key]).filter((wishListItem) => wishListItem).length > 0;
            }

            return (
              !propertyToIgnore.includes(key) &&
              res.data[key] &&
              res.data[key] !== 'unknown' &&
              isNotEmpty(res.data[key])
            );
          });
        res.data.noIndex = !allPerformerCompletedData.length;
      }

      return res.data;
    });
  };

  getPerformerFanClubInfo = (performerName) => {
    const callUrl = ApiService.substitute(PERFORMER_FANCLUB_URL, { username: performerName });

    return ApiService.get(encodeURI(callUrl)).then((res) => res.data);
  };

  getPerformerGifts = (performerName) => {
    const callUrl = ApiService.substitute(GET_PERFORMER_GIFTS_URL, { username: performerName });

    return ApiService.get(encodeURI(callUrl)).then((res) => res.data);
  };

  getPerformerFriendsStatus = (username, performerName) => {
    const callUrl = ApiService.substitute(FRIENDS_STATUS_URL, { username, performerName });

    return ApiService.get(encodeURI(callUrl)).then((res) => res.data);
  };

  handleFriendRequest = (username, performerName, status) => {
    const callUrl = ApiService.substitute(FRIENDS_STATUS_URL, { username, performerName });

    if (status === ENUM_FRIEND_STATUS.NONE) {
      return ApiService.post(encodeURI(callUrl));
    }

    if (status === ENUM_FRIEND_STATUS.PENDING) {
      return ApiService.delete(encodeURI(callUrl));
    }

    if (status === ENUM_FRIEND_STATUS.ACCEPTED) {
      return ApiService.delete(encodeURI(callUrl));
    }

    return undefined;
  };

  getPerformerFavouritesStatus = (username, performerName) => {
    const callUrl = ApiService.substitute(FAVOURITES_BASE, { username, performerName });

    return ApiService.get(encodeURI(callUrl)).then((res) => res.data);
  };

  getPerformerFavouritesCount = (performerName) => {
    const callUrl = ApiService.substitute(FAVOURITES_COUNT, { performerName });

    return ApiService.get(encodeURI(callUrl)).then((res) => res.data);
  };

  handleToggleFavorite = (username, performerName, status) => {
    const callUrl = ApiService.substitute(FAVOURITES_BASE, { username, performerName });

    if (status === ENUM_FAVOURITE_STATUS.FAVORITE) {
      return ApiService.delete(callUrl);
    }

    if (status === ENUM_FAVOURITE_STATUS.NOT_FAVORITE) {
      return ApiService.post(callUrl);
    }

    return undefined;
  };

  getGalleries = async(performerName) => {
    const { user: { sfw } } = UserStore;

    const callUrl = ApiService.substitute(PERFORMER_GALLERIES_URL_NEW, { username: performerName });
    this.messagingService.emit(GET_GALLERIES_RESPONSE_STATE, APIStates.PENDING);

    try {
      const galleriesURL = new URL(encodeURI(callUrl));
      const { data: allGalleries } = await ApiService.get(galleriesURL.toString());

      if (sfw) {
        galleriesURL.searchParams.append('imageStatus', 'SFW');
        const { data: sfwGalleries } = await ApiService.get(galleriesURL.toString());

        const sfwGallery = sfwGalleries.find(({ galleryType }) => galleryType === 'SAFE_FOR_WORK');

        if (sfwGallery) {
          const sfwIndex = allGalleries.findIndex(({ galleryId }) => galleryId === sfwGallery.galleryId);

          if (sfwIndex >= 0) {
            allGalleries[sfwIndex] = sfwGallery;
          } else {
            allGalleries.unshift(sfwGallery);
          }
        }
      }

      this.messagingService.emit(GET_GALLERIES_RESPONSE, allGalleries);
      this.messagingService.emit(GET_GALLERIES_RESPONSE_STATE, APIStates.LOADED);

    } catch (err) {
      this.messagingService.emit(GET_GALLERIES_ERROR, err);
      this.messagingService.emit(GET_GALLERIES_RESPONSE_STATE, APIStates.ERROR);
    }
  };

  getUpcomingShows = (performerName) => {
    const numberOfDays = 90;
    const callUrl = ApiService.substitute(UPCOMING_SHOWS_URL, { username: performerName });
    const startDate = moment().toISOString();
    const endDate = moment().add(numberOfDays, 'days').toISOString();
    const params = {
      startDate,
      endDate,
    };

    return ApiService.get(encodeURI(callUrl), { params }).then((res) => res.data);
  };

  getWishlist = (username) => {
    const parsedUrl = encodeURI(ApiService.substitute(WISHLIST_URL, { username }));
    ApiService.get(parsedUrl).then((res) => {
      this.messagingService.emit(WISHLIST_INFO_RESPONSE, res.data);
    });
  };

  getStreamingInfo = (performerName) => {
    const { isAllowedStreamInfoCall, streamingProvider } = FirebaseStore;
    if (!isAllowedStreamInfoCall || streamingProvider === STREAM_PROVIDERS.CEEBLUE) {
      return;
    }

    this.messagingService.emit(GET_STREAMING_INFO_RESPONSE, undefined, performerName);
    const callUrl = ApiService.substitute(STREAMING_INFO_URL, { username: performerName });
    ApiService.get(encodeURI(callUrl))
      .then((res) => {
        this.messagingService.emit(GET_STREAMING_INFO_RESPONSE, res.data, performerName);
      })
      .catch((err) => {
        this.messagingService.emit(GET_STREAMING_INFO_ERROR, err, performerName);
      });
  };

  getPeekStreamingInfo = (performerName) => {
    const callUrl = STREAMING_INFO_URL.replace('{username}', performerName);
    ApiService.get(encodeURI(callUrl))
      .then((res) => {
        const info = { ...res.data, performerName };
        this.messagingService.emit(GET_PEEK_STREAMING_INFO_RESPONSE, info);
      })
      .catch((err) => {
        this.messagingService.emit(GET_PEEK_STREAMING_INFO_ERROR, { err, performerName });
      });
  };

  getSimilarCams = (username, performerName, gender) => {
    const params = {
      recommendationsForUser: encodeURIComponent(username),
      recommendationsRoomName: encodeURIComponent(performerName),
      resultsPerPage: SIMILAR_CAMS_COUNT,
      gender: encodeURIComponent(gender),
    };
    ApiService.get(DIRECTORY_URL, { params })
      .then((res) => {
        this.messagingService.emit(GET_SIMILAR_CAMS, res.data);
      })
      .catch((err) => {
        parseCatch(err);
      });
  };

  loadConversations = (data) => {
    const parsedURL = encodeURI(`${LOAD_CONVERSATIONS_URL}${generateQueryFromObject(data)}`);

    return ApiService.put(parsedURL)
      .then((res) => {
        if (res?.data?.status === REQUEST_STATUS.SUCCESS) {
          return res.data;
        }

        return null;
      })
      .catch((error) => error);
  };

  postPrivateMessage = (data) => {
    const parsedURL = encodeURI(LOAD_CONVERSATIONS_URL);

    return ApiService.post(parsedURL, {
      data: qs.stringify(data),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    })
      .then((res) => {
        if (res?.data?.status === REQUEST_STATUS.SUCCESS) {
          return res.data;
        }

        return null;
      })
      .catch((error) => error);
  };

  getUploadUrl = (data) =>
    ApiService.post(FILE_UPLOAD_URL, {
      data: qs.stringify(data),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    })
      .then((res) => res.data)
      .catch((error) => {
        const status = error.response && error.response.status;
        if (status === StatusCodes.REQUEST_TIMEOUT) {
          throw new Error(ERRORS.REQUEST_TIME_OUT);
        }
        throw error;
      });

  putFileToCDN = (url, file) => ApiService.put(url, {
    data: file,
    headers: {
      'content-type': file.type,
      'x-amz-acl': 'public-read',
    },
    withCredentials: false,
  })
    .then((res) => res.data)
    .catch((error) => {
      throw error;
    });

  getSilenceList = (username) => {
    const callUrl = ApiService.substitute(GET_SILENCE_LIST_URL, { username });
    ApiService.get(encodeURI(callUrl))
      .then((res) => {
        this.messagingService.emit(GET_SILENCE_LIST_RESPONSE, res.data);
      })
      .catch((err) => {
        this.messagingService.emit(GET_SILENCE_LIST_ERROR, err);
      });
  };

  silenceUser = ({ username, silenceTarget, silenceOrUnsilence }) => {
    const callUrl = ApiService.substitute(SILENCE_USER_URL, { username, silenceTarget });
    const apiCall = silenceOrUnsilence ? ApiService.post(encodeURI(callUrl)) : ApiService.delete(encodeURI(callUrl));
    this.messagingService.emit(GET_SILENCE_USER_RESPONSE_STATUS, { status: APIStates.PENDING });
    apiCall
      .then(() => this.messagingService.emit(GET_SILENCE_USER_RESPONSE_STATUS, { status: APIStates.LOADED, username }))
      .catch((err) => {
        this.messagingService.emit(GET_SILENCE_USER_RESPONSE_STATUS, { status: APIStates.ERROR });
        parseCatch(err);
      });
  };

  markAsSpamMessage = (data) => ApiService.post(encodeURI(WEBCHAT_SEND_NOTIFICATION), {
    data: qs.stringify(data),
    headers: { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' },
  });

  silenceOrBanUserAsRoomModerator = ({ roomname, username, accessHash, accessLevel, type, recipient }) => {
    const callUrl = WEBCHAT_SEND_NOTIFICATION;
    const data = {
      roomname,
      username,
      accessHash,
      accessLevel,
      type,
      recipient,
    };
    const headers = { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' };
    this.messagingService.emit(GET_SILENCE_USER_RESPONSE_STATUS, { status: APIStates.PENDING });
    ApiService.post(encodeURI(callUrl), { data: qs.stringify(data), headers })
      .then(() => this.messagingService.emit(GET_SILENCE_USER_RESPONSE_STATUS, { status: APIStates.LOADED, username }))
      .catch((err) => {
        this.messagingService.emit(GET_SILENCE_USER_RESPONSE_STATUS, { status: APIStates.ERROR });
        parseCatch(err);
      });
  };

  reportUser = ({ abuseType, abuseSubtype, comment, reportedUsername, broadcasterUsername }) => {
    const data = {
      abuseType,
      abuseSubtype,
      comment,
      reportedUsername,
      broadcasterUsername,
    };
    this.messagingService.emit(GET_REPORT_USER_RESPONSE_STATUS, APIStates.PENDING);
    ApiService.post(encodeURI(REPORT_USER_URL), { data })
      .then((response) => {
        if (response.data.status === 'success') {
          this.messagingService.emit(GET_REPORT_USER_RESPONSE_STATUS, APIStates.LOADED);
          NotificationService.create({
            type: NotificationService.type.TOAST,
            text: translate('v2profile.report.sent.successfully'),
            subtype: NotificationService.subtype.SUCCESS,
          });
        }
      })
      .catch(({ response }) => {
        this.messagingService.emit(GET_REPORT_USER_RESPONSE_STATUS, APIStates.ERROR);
        if (response.data.status === 'no_conversations') {
          NotificationService.create({
            type: NotificationService.type.TOAST,
            text: translate('v2profile.report.dont.have.chat.history'),
            subtype: NotificationService.subtype.ERROR,
          });
        } else {
          NotificationService.create({
            type: NotificationService.type.TOAST,
            text: translate('v2profile.notification.error.default'),
            subtype: NotificationService.subtype.ERROR,
          });
        }
      });
  };

  getReportUserInfo = (params) => {
    const callUrl = `${REPORT_USER_URL}/access`;
    this.messagingService.emit(GET_REPORT_USER_INFO_RESPONSE_STATUS, APIStates.INITIAL);
    ApiService.get(encodeURI(callUrl), { params })
      .then((response) => {
        if (response.data.status === 'success') {
          this.messagingService.emit(GET_REPORT_USER_INFO_RESPONSE, false);
        }
        this.messagingService.emit(GET_REPORT_USER_INFO_RESPONSE_STATUS, APIStates.LOADED);
      })
      .catch(({ response }) => {
        if (
          response.data.status === 'too_many_reports_user' ||
          response.data.status === 'too_many_consecutive_reports'
        ) {
          this.messagingService.emit(GET_REPORT_USER_INFO_RESPONSE, true);
        }
        this.messagingService.emit(GET_REPORT_USER_INFO_RESPONSE_STATUS, APIStates.ERROR);
      });
  };

  getPhotos = ({ username, galleryId, isSFWMode }) => {
    this.messagingService.emit(GET_PHOTOS_RESPONSE_STATE, APIStates.PENDING);

    const searchParams = new URLSearchParams();
    searchParams.append('status', 'SFW');
    if (!isSFWMode) {
      searchParams.append('status', 'NSFW');
    }

    const baseUrl = IMAGES_URL_NEW.replace('{status}', searchParams.toString());
    const url = ApiService.substitute(baseUrl, { username: encodeURIComponent(username), galleryId });
    ApiService.get(url)
      .then((res) => {
        this.messagingService.emit(GET_PHOTOS_RESPONSE, res.data);
        this.messagingService.emit(GET_PHOTOS_RESPONSE_STATE, APIStates.LOADED);
      })
      .catch(() => {
        this.messagingService.emit(GET_PHOTOS_RESPONSE_STATE, APIStates.ERROR);
      });
  };

  getUsersFriends = (username) => {
    const URL = ApiService.substitute(FRIENDLIST_URL, { username: encodeURIComponent(username) });
    ApiService.get(URL, { params: { offset: 0, limit: 15 } })
      .then((response) => {
        if (response.status === StatusCodes.OK) {
          this.messagingService.emit(
            GET_USER_FRIENDS_RESPONSE,
            response.data
          );
        }
      })
      .catch((err) => {
        parseCatch(err);
      });
  };

  getSexQuestionnaire = (performerName) => {
    const URL = SEX_QUESTIONNAIRES_URL.replace('/{username}', '');
    ApiService.get(URL)
      .then((res) => {
        if (res && res.data) {
          this.getSexQuestionnaireAnswers(res.data, performerName);
        }
      })
      .catch((e) => {
        parseCatch(e);
      });
  };

  getSexQuestionnaireAnswers = (questions, performerName) => {
    const URL = ApiService.substitute(SEX_QUESTIONNAIRES_URL, { username: encodeURIComponent(performerName) });
    ApiService.get(URL)
      .then((res) => {
        if (res?.data) {
          const answersIds = Object.keys(res.data);
          const sexQuestionnaires = questions.map((item) => {
            item.answer = '';
            answersIds.forEach((answer) => {
              if (item.questionnaireId === Number(answer)) {
                item.answer = res.data[answer];
              }
            });

            return item;
          });

          this.messagingService.emit(GET_SEX_QUESTIONNAIRE_RESPONSE, sexQuestionnaires);
        }
      })
      .catch((e) => {
        parseCatch(e);
      });
  };

  _featureBroadcasterAction = (dataFeatureBroadcaster) => {
    ApiService.post(
      WEBCHAT_SEND_NOTIFICATION, {
        data: qs.stringify(dataFeatureBroadcaster),
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
      })
      .catch((error) => parseCatch(error));
  };

  _closeRoomAction = (dataCloseRoom) => {
    ApiService.post(
      WEBCHAT_SEND_NOTIFICATION, {
        data: qs.stringify(dataCloseRoom),
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
      })
      .catch((error) => parseCatch(error));
  };

  sendProfileFeedback = (username, data) => {
    this.messagingService.emit(POST_FEEDBACK_V2_PROFILE_RESPONSE_STATUS, APIStates.INITIAL);
    const URL = ApiService.substitute(PROFILE_V2_FEEDBACK_URL, { username: encodeURI(username) });
    ApiService.post(URL, { data })
      .then(() => {
        this.messagingService.emit(POST_FEEDBACK_V2_PROFILE_RESPONSE_STATUS, APIStates.LOADED);
        this.messagingService.emit(SEND_USER_FEEDBACK_MODAL);
      })
      .catch((error) => {
        this.messagingService.emit(POST_FEEDBACK_V2_PROFILE_RESPONSE_STATUS, APIStates.ERROR);
        parseCatch(error);
      });
  };

  getFeaturedCamStats = (username) => {
    ApiService.get(BROADCAST_FEATURED, { params: { username } })
      .then((res) => {
        this.messagingService.emit(GET_FEATURED_CAM_STATS, res.data.data);
      })
      .catch((err) => parseCatch(err));
  };
}

export default new ProfileService(MessagingService);
